'use client'

import React, { useCallback, useState } from 'react'
import { useTranslations } from 'next-intl'

import type { HolidayImage } from '@/src/types/holidays'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'

import PhotoLightboxWrapper from '../photoLightbox'

import PackageHero from './packageHero'

interface PackageHeroWrapperProps {
    gallery: HolidayImage[]
    isMobile: boolean
}

/**
 * The hero shows a lead image and, on desktop, the next two beside it. Everything else in the
 * gallery is reachable through the lightbox, which is what "Show all photos" opens.
 */
const PackageHeroWrapper: React.FC<PackageHeroWrapperProps> = ({ gallery, isMobile }) => {
    const t = useTranslations('holidaysPage')
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()
    const [activeIndex, setActiveIndex] = useState<number | null>(null)

    const images = (gallery ?? []).filter(image => Boolean(image?.url))
    const [lead, ...rest] = images

    // Opening the lightbox is the gallery engagement; stepping through it afterwards is not
    // reported separately, or one reader browsing the photos would flood the funnel.
    const handleOpenAt = useCallback(
        (index: number) => {
            setActiveIndex(index)
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: 'gallery',
                engagement_type: 'open',
                photo_index: index + 1,
                photo_count: images.length
            })
        },
        [triggerHolidayUIActionEvent, images.length]
    )

    const handleClose = useCallback(() => setActiveIndex(null), [])

    if (!lead) return null

    return (
        <>
            <PackageHero
                images={images}
                lead={lead}
                tiles={rest.slice(0, 2)}
                showAllLabel={t('showAllPhotos')}
                openGalleryLabel={t('openGallery')}
                onOpenAt={handleOpenAt}
                isMobile={isMobile}
            />
            <PhotoLightboxWrapper
                images={images}
                activeIndex={activeIndex}
                onChangeIndex={setActiveIndex}
                onClose={handleClose}
                isMobile={isMobile}
            />
        </>
    )
}

export { PackageHeroWrapper }
export default PackageHeroWrapper
